export type ProfilerLog = {
  url: string
  method: string
  status: number
  duration: number
  sizeBytes: number
  timestamp: string
  page?: string
}

export type ProfilerRequestEvent = {
  path?: string
  node?: {
    req?: {
      url?: string
      headers?: {
        cookie?: string
      }
    }
  }
  context?: Record<string, unknown>
}

const REQUEST_LOG_KEY = '__vaProfilerRequestLogs__'
const REQUEST_ENABLED_KEY = '__vaProfilerEnabled__'
const PROFILER_LOGS_STATE = 'va-profiler-request-logs'
const PROFILER_PAGE_STATE = 'va-profiler-request-page'

export function estimateValueSizeBytes(value: unknown) {
  if (value == null) {
    return 0
  }

  if (typeof value === 'string') {
    return new TextEncoder().encode(value).length
  }

  try {
    return new TextEncoder().encode(JSON.stringify(value)).length
  } catch {
    return 0
  }
}

function hasProfilerCookie(cookieHeader?: string) {
  return (cookieHeader || '')
    .split(';')
    .some((part) => part.trim().startsWith('VASECRET_PROFILER='))
}

function resolveProfilerEnabled(nuxtApp?: ReturnType<typeof tryUseNuxtApp>, presetEnabled = false) {
  if (presetEnabled) {
    return true
  }

  const readEnabledState = () => useState<boolean>('va-profiler-enabled', () => false).value === true

  try {
    return readEnabledState()
  } catch {
    if (nuxtApp) {
      try {
        return nuxtApp.runWithContext(readEnabledState)
      } catch {
        return false
      }
    }

    return false
  }
}

export async function appendServerProfilerLog(
  log: ProfilerLog,
  context?: {
    requestEvent?: ProfilerRequestEvent
    profilerEnabled?: boolean
  }
) {
  if (!import.meta.server) {
    return
  }

  const nuxtApp = tryUseNuxtApp()

  let requestEvent: ProfilerRequestEvent | undefined = context?.requestEvent
  if (!requestEvent && nuxtApp?.ssrContext?.event) {
    requestEvent = nuxtApp.ssrContext.event as unknown as ProfilerRequestEvent
  }

  const stateEnabled = resolveProfilerEnabled(nuxtApp, context?.profilerEnabled === true)
  const requestEnabledFromNuxtEvent = requestEvent?.context?.[REQUEST_ENABLED_KEY] === true
  const hasProfilerCookieFromNuxtEvent = hasProfilerCookie(requestEvent?.node?.req?.headers?.cookie)

  // Fast exit: avoid resolving request event in disabled path.
  if (!stateEnabled && !requestEnabledFromNuxtEvent && !hasProfilerCookieFromNuxtEvent) {
    return
  }

  try {
    if (!requestEvent) {
      requestEvent = useRequestEvent() as unknown as ProfilerRequestEvent
    }
  } catch {
    if (!requestEvent) {
      return
    }
  }

  const requestEnabled = requestEvent?.context?.[REQUEST_ENABLED_KEY] === true
  const hasProfilerCookieFromResolvedEvent = hasProfilerCookie(requestEvent?.node?.req?.headers?.cookie)

  if (!stateEnabled && !requestEnabled && !hasProfilerCookieFromResolvedEvent) {
    return
  }

  if (!requestEvent.context) {
    requestEvent.context = {}
  }

  const current = Array.isArray(requestEvent.context[REQUEST_LOG_KEY])
    ? (requestEvent.context[REQUEST_LOG_KEY] as ProfilerLog[])
    : []

  const next = [...current, log]
  if (next.length > 200) {
    next.shift()
  }

  requestEvent.context[REQUEST_LOG_KEY] = next

  const syncState = () => {
    const profilerState = useState<ProfilerLog[]>(PROFILER_LOGS_STATE, () => [])
    profilerState.value = [...next].sort((a, b) => {
      const aStart = new Date(a.timestamp).getTime() - Number(a.duration || 0)
      const bStart = new Date(b.timestamp).getTime() - Number(b.duration || 0)
      return aStart - bStart
    })

    const profilerPageState = useState<string>(PROFILER_PAGE_STATE, () => '')
    profilerPageState.value =
      requestEvent?.node?.req?.url?.split('?')[0] || requestEvent?.path || log.page || ''
  }

  try {
    syncState()
  } catch {
    if (nuxtApp) {
      try {
        nuxtApp.runWithContext(syncState)
      } catch {
        // In non-setup server contexts, keep logs only in request event context.
      }
    }
  }
}
